import { ReactNode, useState, useEffect, useMemo } from "react";

import { Layout, Menu } from "antd";
import { clsx } from "clsx";
import { Link, useLocation, matchPath } from "react-router-dom";

import { RVIcon, RVIconSizeOptions } from "../../atoms";

export type NavItem = {
    href: string;
    title: string | ReactNode;
    key: string;
    isVisible?: boolean;
    // a list of URLs that are considered part of this item
    // if any of them match the current url
    // this item will be selected
    tabs?: Array<string>;
    // treat `href` as an app-absolute path rather than one relative to `basePath`,
    // for shortcuts that link out of the current section
    isAbsoluteHref?: boolean;
    // optional click handler (e.g. expand a destination section's subnav before navigating)
    onClick?: () => void;
    // open in a new tab and show an "opens elsewhere" icon. Use for shortcuts that leave the
    // current section, so deep in-section work isn't lost behind a back button.
    opensInNewTab?: boolean;
};

export type PageSubNavItem = NavItem & {
    children?: Array<NavItem>;
};

import styles from "./RVSideNavLayout.module.scss";

type SidenavItemsProps = {
    sidenavTitle: string;
    items: Array<PageSubNavItem>;
    basePath: string;
    hasSubPages?: boolean;
    defaultOpenKeys?: string[];
};

export function RVSideNavLayout({
    sidenavTitle,
    items,
    basePath,
    hasSubPages,
    defaultOpenKeys,
}: SidenavItemsProps): JSX.Element {
    const { pathname } = useLocation();
    const resolveHref = (item: NavItem) => (item.isAbsoluteHref ? item.href : `${basePath}${item.href}`);

    const renderNavLink = (item: NavItem) =>
        item.opensInNewTab ? (
            <Link
                to={resolveHref(item)}
                onClick={item.onClick}
                className={styles.newTabLink}
                target="_blank"
                rel="noopener noreferrer"
            >
                {item.title}
                <RVIcon icon="expand" size={RVIconSizeOptions.SMALL} className={styles.newTabIcon} />
            </Link>
        ) : (
            <Link to={resolveHref(item)} onClick={item.onClick}>
                {item.title}
            </Link>
        );

    let selectedItem: NavItem | undefined;

    if (!hasSubPages) {
        selectedItem = items
            .flatMap(
                // if it has children, the submenu title is not a link anymore,
                // so it should not be selectable
                (item) => (item.children ? [...item.children] : [item])
            )
            .find(
                // match the side menu item by its href
                // or, if it has a list of tabs, find a matching tab
                // this allows the same menu item to be highlighted for multiple URLs
                (item) =>
                    matchPath(resolveHref(item), pathname) ||
                    item.tabs?.find((tab) => matchPath(basePath + tab, pathname))
            );
    } else {
        // Check all items and their children for matches
        selectedItem = items
            .flatMap((item) => (item.children ? [...item.children] : [item]))
            .find(
                (item) =>
                    matchPath(resolveHref(item), pathname) ||
                    item.tabs?.find((tab) => matchPath(basePath + tab, pathname))
            );
    }
    const defaultSelectedKey = items[0]?.key;
    const selectedKey = selectedItem?.key ?? defaultSelectedKey;

    // Find which submenus should be open based on selected item
    const autoOpenKeys = useMemo(
        () => items.filter((item) => item.children?.some((child) => child.key === selectedKey)).map((item) => item.key),
        [items, selectedKey]
    );

    // State to manage which submenus are open (allows manual toggling + auto-expand on navigation)
    const [openKeys, setOpenKeys] = useState<string[]>(defaultOpenKeys || autoOpenKeys);

    useEffect(() => {
        // Only add new autoOpenKeys, don't remove existing ones
        setOpenKeys((prevKeys) => {
            const newKeys = [...(defaultOpenKeys || []), ...autoOpenKeys];
            // Merge with existing keys, removing duplicates
            return Array.from(new Set([...prevKeys, ...newKeys]));
        });
    }, [autoOpenKeys, defaultOpenKeys]);

    // Only close submenus when explicitly toggled, not when clicking items
    const handleOpenChange = (keys: string[]) => {
        // If keys array is smaller, user is closing a submenu
        if (keys.length < openKeys.length) {
            setOpenKeys(keys);
        } else {
            // User is opening a submenu, merge with existing
            setOpenKeys(Array.from(new Set([...openKeys, ...keys])));
        }
    };

    return (
        <Layout.Sider className={styles.sidebarSettings}>
            <h2 className={styles.settingsMenuTitle}>{sidenavTitle}</h2>

            <Menu
                style={{ backgroundColor: "#6a6acb" }}
                selectedKeys={selectedKey ? [selectedKey] : []}
                openKeys={openKeys}
                onOpenChange={handleOpenChange}
                theme="dark"
                mode="inline"
            >
                {items.map((item) => {
                    return (
                        (item.isVisible === undefined || item.isVisible) &&
                        (item.children && item.children.length > 0 ? (
                            <Menu.SubMenu key={item.key} title={item.title} className={clsx(styles.submenu)}>
                                {item.children.map(
                                    (subitem) =>
                                        (subitem.isVisible === undefined || subitem.isVisible) && (
                                            <Menu.Item
                                                className={clsx(styles.submenuItem, {
                                                    [styles.selected]: subitem.key === selectedKey,
                                                })}
                                                key={subitem.key}
                                            >
                                                {renderNavLink(subitem)}
                                            </Menu.Item>
                                        )
                                )}
                            </Menu.SubMenu>
                        ) : (
                            <Menu.Item
                                className={clsx(styles.menuItem, {
                                    [styles.selected]: item.key === selectedKey,
                                })}
                                key={item.key}
                            >
                                {renderNavLink(item)}
                            </Menu.Item>
                        ))
                    );
                })}
            </Menu>
        </Layout.Sider>
    );
}
